import React, { useState, useEffect, useCallback } from "react";
import { Button } from "react-bulma-components";
import { useAuth } from "../context/AuthContext";
import { useLayoutContext } from "../context/LayoutContext";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CircleArrowLeft, UndoDot, XCircle } from "lucide-react";
import ProductDetails from "../components/CompoundComparePage/ProductDetail";
import Image from "../components/common/Image";
import api from "../hooks/api";
import { API_URLS, OrderComponets } from "../App";
import { useFetchData } from "../hooks/useFetchData";
import usePCComparison from "../hooks/usePCComparison";

const CompoundComparePage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { pcIds } = useParams<{ pcIds?: string }>();
  const { setSystemError, setLoading, setModalScreen } = useLayoutContext();

  const { selectedPCIdsToCompare, handleAddToCompare } = usePCComparison();
  const [pcsToCompare, setPcsToCompare] = useState<Compound[]>([]);
  const sortComponents = OrderComponets.map((item) => item[0]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login", { state: { from: pathname } });
    }
  }, [isAuthenticated(), navigate, pathname]);

  const fetchPCs = async (ids: string[]) => {
    setLoading({ showSpinner: true, style: "fixed" });
    setSystemError(null);
    try {
      const fetchedPCs = await Promise.all(
        ids.map(async (id) => {
          return api.get(API_URLS.CATALOG + `/${encodeURIComponent(id)}`);
        }),
      );
      setPcsToCompare(fetchedPCs.map((res) => res.data));
    } catch (err: any) {
      setSystemError({
        message: "Não foi possível buscar as configurações para comparação.",
        code: err.code || "FETCH_ERROR",
        details: err.message || "Erro desconhecido ao buscar PCs.",
        retryAfter: () => {
          if (!pcIds) {
            navigate(-1);
            return;
          }
          fetchPCs(pcIds.split(";").filter(Boolean));
        },
      });
      console.warn("Error fetching PCs for comparison:", err);
    } finally {
      setLoading(false);
    }
  };

  const showComponentDetails = (selectedProduct: any) => {
    setModalScreen({
      state: "view",
      title: `Detalhes do componente`,
      children: <ProductDetails reference={selectedProduct.reference} />,
      onClose: () => setModalScreen(null),
    });
  };

  const getComponentName = (pc: Compound[], component: string) => {
    return pc
      .filter((p) => p.components?.[component]?.[0]?.baseComponent)
      .map((p) => p.components[component][0].type)
      .pop();
  };

  const getOtherComponents = (pc: Compound[]) => {
    return [
      ...new Set(pc.flatMap((p: Compound) => Object.keys(p.components || {}))),
    ].filter((key) => !sortComponents.includes(key));
  };

  const getNestedProperty = (
    obj: any,
    path: string,
    defaultValue: any = "N/A",
  ) => {
    return path.split(".").reduce((acc, part) => {
      if (part.endsWith("[0]")) {
        const arrayKey = part.slice(0, -3);
        if (acc && Array.isArray(acc[arrayKey]) && acc[arrayKey].length > 0) {
          return acc[arrayKey][0];
        }
        return defaultValue;
      }
      return acc && acc[part] !== undefined ? acc[part] : defaultValue;
    }, obj);
  };

  useEffect(() => {
    if (pcIds) {
      const ids = pcIds.split(";").filter(Boolean);
      handleAddToCompare(ids);
      if (ids.length >= 2) {
        handleAddToCompare(ids);
        fetchPCs(ids);
      } else {
        setSystemError({
          message: "Erro: É necessário comparar pelo menos duas configurações.",
          code: "INSUFFICIENT_PCS",
          details: "Foram fornecidos menos de dois IDs de PCs para comparação.",
          retryAfter: () => {
            navigate(-1);
          },
        });
        setLoading(false);
      }
    } else if (selectedPCIdsToCompare.length >= 2) {
      fetchPCs(selectedPCIdsToCompare);
    }
  }, [pcIds]);

  const handleRemoveFromCompare = (id: string) => {
    const updatedSelectedPCIds = selectedPCIdsToCompare.filter(
      (pcId) => pcId !== id,
    );
    handleAddToCompare(updatedSelectedPCIds);
    setPcsToCompare(
      pcsToCompare.filter((pc) => updatedSelectedPCIds.includes(pc.id)),
    );
    history.replaceState({}, "", `/comparar/${updatedSelectedPCIds.join(";")}`);
  };

  const handleClose = () => {
    navigate(-1);
  };

  const displayComponentItem = (
    component: string,
    idx: number,
    componentName: string,
  ) => {
    return (
      <tr key={idx}>
        <td className="px-4 py-2 font-semibold">{componentName}</td>
        {pcsToCompare.map((pc) => {
          const quantity = getNestedProperty(
            pc,
            `components.${component}[0].quantity`,
          );
          const value = getNestedProperty(
            pc,
            `components.${component}[0].name`,
          );
          const reference = getNestedProperty(
            pc,
            `components.${component}[0].reference`,
            null,
          );
          const image = getNestedProperty(
            pc,
            `components.${component}[0].image`,
            null,
          );
          return (
            <td
              key={pc.id}
              className={`px-4 py-2 ${reference ? "cursor-pointer" : ""}`}
              onClick={() => {
                reference &&
                  showComponentDetails({
                    reference: reference,
                    name: value,
                  });
              }}
            >
              <span className="w-full text-center block">
                {parseInt(quantity) > 1 ? quantity + " x " : ""}
                {value}
              </span>
            </td>
          );
        })}
      </tr>
    );
  };

  if (!pcsToCompare || pcsToCompare.length < 2) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <p className="text-red-500">Não existe computadores para comparação</p>
        <Button onClick={handleClose} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto print-container">
      <div className="p-4 overflow-auto">
        <div className="text-2xl font-bold mb-4 pt-5 items-center">
          <Link
            to={`/`}
            className="not-printable"
            onClick={(e) => {
              e.preventDefault();
              history.back();
            }}
          >
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="ml-5 mr-5 text-red-500 hover:text-red-700 flex items-center"
            >
              <CircleArrowLeft className="w-7 h-7 inline-block" />
              <span className="pl-2">Voltar</span>
            </motion.button>
          </Link>
        </div>
        <div className="table-container overflow-x-auto bg-white rounded-lg shadow-md">
          <table className="min-w-full table is-narrow is-hoverable">
            <thead>
              <tr>
                <th className="px-4 py-2 w-50"></th>
                {pcsToCompare.map((pc) => (
                  <th key={pc.id} className="px-4 py-2">
                    <div className="flex justify-between items-start">
                      <span
                        className="block w-full text-center"
                        onClick={() => {
                          navigate("/pc/" + pc.id);
                        }}
                      >
                        {pc.category_name}
                        <br />
                        {pc.id}
                      </span>
                      <span className="is-danger">
                        <XCircle
                          className={`inline-block w-6 h-6 ml-2 ${pcsToCompare.length <= 2 ? "text-gray-500 cursor-not-allowed" : "text-red-500 cursor-pointer"}`}
                          onClick={(e) => {
                            e.preventDefault();
                            if (pcsToCompare.length <= 2) return;
                            handleRemoveFromCompare(pc.id);
                          }}
                        />
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2  font-semibold"></td>
                {pcsToCompare.map((pc) => (
                  <td key={pc.id} className="px-4 py-2">
                    <Image
                      onClick={() => {
                        navigate("/pc/" + pc.id);
                      }}
                      loading="progressive"
                      src="https://placehold.co/400"
                      dataSrc={
                        pc.image && pc.image.trim() !== ""
                          ? pc.image.trim()
                          : "https://placehold.co/400"
                      }
                      sizes="400x400"
                      alt={pc.name ?? "PC Image"}
                      className="w-32 h-32 object-cover m-auto rounded"
                    />
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2  font-semibold">Descrição</td>
                {pcsToCompare.map((pc) => (
                  <td key={pc.id} className="px-4 py-2">
                    <span
                      className="block w-full text-center"
                      onClick={() => {
                        navigate("/pc/" + pc.id);
                      }}
                    >
                      {pc.description}
                    </span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2  font-semibold">Preço</td>
                {pcsToCompare.map((pc) => {
                  const value = getNestedProperty(pc, "price.value");
                  const currency = getNestedProperty(pc, "price.currency");
                  return (
                    <td key={pc.id} className="px-4 py-2  text-center">
                      <span
                        className="block w-full text-center"
                        onClick={() => {
                          navigate("/pc/" + pc.id);
                        }}
                      >
                        {Math.round(value * 100) / 100} {currency}
                      </span>
                    </td>
                  );
                })}
              </tr>
              {sortComponents.map((component, idx) => {
                const componentName = getComponentName(pcsToCompare, component);
                if (componentName) {
                  return displayComponentItem(component, idx, componentName);
                }
                return null;
              })}
              {getOtherComponents(pcsToCompare).map((component, idx) => {
                const componentName = getComponentName(pcsToCompare, component);
                if (componentName) {
                  return displayComponentItem(component, idx, componentName);
                }
                return null;
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompoundComparePage;
